/**
 * DeviceRouter — selects the right layout based on window width and device type.
 *
 * Decision:
 *   Capacitor native app   → CapacitorLayout
 *   iPad / Android tablet  → TabletLayout    (both portrait + landscape)
 *   Window >= 1024px wide  → DesktopLayout   (full persistent sidebar)
 *   Window < 1024px        → MobileLayout    (with browserType + hasBrowserChrome props)
 *
 * iPad note (Apr 2026): iPads were previously hitting MobileLayout in
 * portrait (834px) and DesktopLayout in landscape (1194px) — two
 * different experiences on the same device as the user rotates. Now
 * tablet UA detection catches both orientations and routes to the
 * purpose-built TabletLayout, which has:
 *   - persistent sidebar sized for touch (200/240px)
 *   - content pane that uses the full remaining width
 *   - visual-viewport observer for Chrome/Firefox Android tablet toolbars
 */

import { useState, useEffect } from 'react';
import MobileLayout    from './MobileLayout.jsx';
import CapacitorLayout from './CapacitorLayout.jsx';
import DesktopLayout   from './DesktopLayout.jsx';
import TabletLayout    from './TabletLayout.jsx';

function getWidth() {
  return typeof window !== 'undefined' ? window.innerWidth : 1280;
}

function isCapacitor() {
  // Must be a native iOS/Android app — 'web' platform means browser PWA, not native
  const cap = window.Capacitor;
  if (!cap) return false;
  if (typeof cap.isNativePlatform === 'function') return cap.isNativePlatform() === true;
  return cap.platform === 'ios' || cap.platform === 'android';
}

function isIOS() {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

// ── Tablet detection (Apr 2026) ──
// A "tablet" is an iPad (any orientation, any iPadOS version) or an
// Android tablet (Android UA WITHOUT the "Mobile" keyword — that's
// Google's recommended way to split phone/tablet Android builds).
// Detection order:
//   1. iPad via UA — catches iPadOS <13 and all third-party browsers
//      that keep "iPad" in their UA string.
//   2. iPad via MacIntel + maxTouchPoints — iPadOS 13+ Safari lies
//      about its platform to match desktop Safari's UA string.
//      navigator.maxTouchPoints > 1 is the reliable signal that
//      separates real Macs (0 touch points) from iPads (5+).
//   3. Android tablet — Android UA with no "Mobile" substring.
//      Chrome/Firefox on Android follow this convention; phones
//      include "Mobile" in their UA and tablets don't.
function isTablet() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad/.test(ua)) return true;
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return true;
  return false;
}

// ── Browser-chrome detection (Apr 2026) ──
// MobileLayout used to receive hasBrowserChrome as a prop but nothing
// ever passed it, so the layout always assumed "Safari auto-hide"
// and used only 12px of bottom padding. That caused content to sit
// under the persistent bottom toolbar on:
//   • Chrome/Firefox Android (always-visible bottom bar)
//   • iPad Safari (bar is persistent)
//   • iPhone Safari with "Always Show Toolbar" accessibility setting
//   • Standalone PWAs (no chrome at all, different math)
//
// Detection strategy:
//   1. Android UA: any Android mobile Chrome/Firefox has persistent
//      chrome. True.
//   2. PWA standalone display mode: no browser chrome. False.
//   3. iOS: compare window.innerHeight with visualViewport.height.
//      If they meaningfully differ, chrome is taking space. This
//      check isn't perfect on iOS because the toolbar auto-hides
//      during scroll, but at INITIAL LOAD the bar is always visible,
//      which is exactly the condition we need to accommodate.
function detectBrowserChrome() {
  if (typeof window === 'undefined') return false;
  try {
    // PWA in standalone mode — no chrome at all
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return false;
    if (window.navigator.standalone === true) return false;

    const ua = window.navigator.userAgent || '';
    // Android browsers: chrome is always persistent. Safer to assume
    // yes and pad accordingly than to get it wrong.
    if (/Android/i.test(ua)) return true;

    // iPhone/iPad Safari/Chrome (all iOS browsers use WebKit).
    // Since the bottom bar is present on page load, we default to
    // `true` and let the scroll container's padding handle it —
    // when Safari does auto-hide the bar, the extra 48-56px of
    // bottom padding is simply blank scroll space, not a layout
    // bug. That's a much better UX tradeoff than content clipped
    // under the bar.
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;

    // Everything else — assume no chrome (narrow desktop windows,
    // kiosk mode, etc.)
    return false;
  } catch {
    return true; // fail safe — extra padding beats clipped content
  }
}

export default function DeviceRouter() {
  const [width, setWidth] = useState(getWidth);
  // Browser-chrome detection result — captured once on mount. Re-
  // evaluating on every resize would risk flicker because orientation
  // changes also resize. The underlying UA signals don't change
  // mid-session, so a single capture is fine.
  const [hasBrowserChrome] = useState(detectBrowserChrome);

  useEffect(() => {
    // Apply --vh CSS variable for mobile viewport height
    const setVH = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
      setWidth(window.innerWidth);
    };
    setVH();
    window.addEventListener('resize', setVH, { passive: true });
    window.addEventListener('orientationchange', setVH, { passive: true });
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // Capacitor native app always gets its own layout
  if (isCapacitor()) {
    return <CapacitorLayout isIOS={isIOS()} isAndroid={/Android/i.test(navigator.userAgent)} />;
  }

  // Apr 2026 — Tablet detection (iPad / Android tablet) comes BEFORE the
  // width-based desktop/mobile split. Without this, iPads in portrait
  // (834px) went to MobileLayout — a cramped phone UI on a 11" screen —
  // and in landscape (1194px) went to DesktopLayout with a sidebar
  // calibrated for mouse-pointer use, not touch. TabletLayout solves both:
  // it uses a 200/240px sidebar with 44px touch targets and renders the
  // content pane at the full remaining width in every orientation.
  // Matches Chrome, Safari, and Firefox on iPad, iPadOS Safari (which
  // spoofs MacIntel), and Android tablets like Galaxy Tab / Pixel Tablet.
  if (isTablet()) {
    return <TabletLayout isIPad={/iPad/.test(navigator.userAgent)
                              || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)} />;
  }

  // Any window 1024px or wider gets the full desktop layout with persistent sidebar
  if (width >= 1024) {
    return <DesktopLayout />;
  }

  // Narrow windows get the mobile layout. Pass hasBrowserChrome so
  // the layout can apply additional bottom padding (Apr 2026 fix —
  // previously these props arrived as undefined and the layout
  // defaulted to the too-small "Safari auto-hides" padding value).
  const browserType = /Android/i.test(navigator.userAgent) ? 'chrome-android'
                   : isIOS() ? 'safari-ios' : 'other';
  return <MobileLayout browserType={browserType} hasBrowserChrome={hasBrowserChrome} />;
}
